// packages/api-client/src/services/admin/adminGoalService.ts

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { 
    ServiceResponse, 
    Goal, 
    GoalSnapshot,
    GoalWithLatestSnapshot 
} from '@dopomogai/types';

const ADMIN_GOAL_PREFIX = '/admin/goals/';

// --- REMOVED ---
// We no longer need the separate `GoalCreatePayload` as the service will handle the mapping.
// type GoalCreatePayload = ...

type ManualSnapshotPayload = {
  value: number;
};

export const adminGoalService = {
  // --- Goal Definition Management ---

  /** Creates or updates a goal definition in the database. */
  createOrUpdateGoalDefinition: (goalData: Goal): ServiceResponse<Goal> => {
    // The component provides a `Goal` object with `_id`.
    // The API endpoint expects a payload with `id`.
    // This service function is the perfect place to perform the mapping.
    const payload = {
      id: goalData._id,
      name: goalData.name,
      description: goalData.description,
      is_manual: goalData.is_manual,
      target_value: goalData.target_value,
    };
    
    return getApiClient().post<Goal>(`${ADMIN_GOAL_PREFIX}definitions`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `creating/updating goal definition ${payload.id}`));
  },

  /** Lists all created goal definitions. (Less used now, but good to keep). */
  listGoalDefinitions: (): ServiceResponse<Goal[]> => {
    return getApiClient().get<Goal[]>(`${ADMIN_GOAL_PREFIX}definitions`)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing goal definitions'));
  },

  /**
   * Fetches all goal definitions and enriches each one with its most recent snapshot.
   */
  getGoalsDashboard: (): ServiceResponse<GoalWithLatestSnapshot[]> => {
    return getApiClient().get<GoalWithLatestSnapshot[]>(`${ADMIN_GOAL_PREFIX}dashboard`)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'fetching goals dashboard data'));
  },

  // --- Snapshot Management ---

  /** Logs a new manual snapshot for a specific goal. */
  logManualSnapshot: (goalId: string, data: ManualSnapshotPayload): ServiceResponse<GoalSnapshot> => {
    return getApiClient().post<GoalSnapshot>(`${ADMIN_GOAL_PREFIX}${goalId}/snapshots`, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `logging snapshot for goal ${goalId}`));
  },
};